import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { useMe } from '../../_shared/hooks/useMe';
import { useStandardTerms } from '../hooks/useStandardTerms';
import { useUpsertStandardTerm } from '../hooks/useUpsertStandardTerm';
import { useDeleteStandardTerm } from '../hooks/useDeleteStandardTerm';
import { StandardTermFormDialog } from '../components/StandardTermFormDialog';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { SimilarityBadge } from '../components/SimilarityBadge';
import { TerminologyDownloadMenu } from '../components/TerminologyDownloadMenu';
import { TerminologyToast, useToast } from '../components/TerminologyToast';
import type { StandardTerm, ApiError, StandardTermSearchParams } from '../types/terminology';
import type { StandardTermFormValues } from '../schema/terminologySchema';

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const SOURCE_OPTIONS = ['ARGOS', 'TO_BE_BSS', 'MIS', 'MANUAL'];

/* ─── Skeleton rows ─────────────────────────────────────────────────────── */
function SkeletonRows({ count = 20 }: { count?: number }) {
  return (
    <div aria-busy="true" aria-label="로딩 중">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            height: 44,
            margin: 'var(--spacing-1) 0',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-sm)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────── */

export function StandardTermListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const meQuery = useMe();
  const isAdmin = meQuery.data?.role === 'ROLE_ADMIN';

  // Search / filter state — initialized from URL query params for deep-link support
  const [keyword, setKeyword] = useState(() => searchParams.get('keyword') ?? '');
  const [appliedKeyword, setAppliedKeyword] = useState(() => searchParams.get('keyword') ?? '');
  const [domain, setDomain] = useState(() => searchParams.get('domain') ?? '');
  const [source, setSource] = useState(() => searchParams.get('source') ?? '');
  const [includeDeprecated, setIncludeDeprecated] = useState(() => searchParams.get('includeDeprecated') === 'true');
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get('page'));
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  const [size, setSize] = useState(20);

  // Debounce keyword
  useEffect(() => {
    const id = setTimeout(() => {
      setAppliedKeyword(keyword.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [keyword]);

  // Sync filter state back to URL (for deep-link / back-button)
  useEffect(() => {
    const next: Record<string, string> = {};
    if (appliedKeyword) next['keyword'] = appliedKeyword;
    if (domain) next['domain'] = domain;
    if (source) next['source'] = source;
    if (includeDeprecated) next['includeDeprecated'] = 'true';
    if (page > 0) next['page'] = String(page);
    setSearchParams(next, { replace: true });
  }, [appliedKeyword, domain, source, includeDeprecated, page, setSearchParams]);

  const params: StandardTermSearchParams = {
    ...(appliedKeyword ? { keyword: appliedKeyword } : {}),
    ...(domain ? { domain } : {}),
    ...(source ? { source } : {}),
    includeDeprecated,
    page,
    size,
  };

  const listQuery = useStandardTerms(params);
  const upsertMutation = useUpsertStandardTerm();
  const deleteMutation = useDeleteStandardTerm();
  const { messages, addToast, dismissToast } = useToast();

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState<StandardTerm | null>(null);
  const [formError, setFormError] = useState<string | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<StandardTerm | null>(null);

  const handleFormOpen = useCallback((term: StandardTerm | null = null) => {
    setFormError(undefined);
    setFormTarget(term);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setFormTarget(null);
    setFormError(undefined);
  }, []);

  const handleFormSubmit = useCallback(
    (values: StandardTermFormValues) => {
      setFormError(undefined);
      upsertMutation.mutate(
        {
          id: formTarget?.id,
          body: {
            korean: values.korean,
            englishShort: values.englishShort,
            englishFull: values.englishFull || undefined,
            description: values.description || undefined,
            domain: values.domain || undefined,
            source: values.source || undefined,
            status: values.status,
          },
        },
        {
          onSuccess: () => {
            handleFormClose();
            addToast(
              formTarget
                ? t('terminologyManagement.toast.updated')
                : t('terminologyManagement.toast.created')
            );
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            const msgKey = `terminologyManagement.error.${apiErr.code}`;
            const translated = t(msgKey);
            setFormError(translated !== msgKey ? translated : apiErr.message);
          },
        }
      );
    },
    [formTarget, upsertMutation, handleFormClose, addToast, t]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { id: deleteTarget.id },
      {
        onSuccess: () => {
          setDeleteTarget(null);
          addToast(t('terminologyManagement.toast.deprecated'));
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          setDeleteTarget(null);
          addToast(apiErr.message ?? t('terminologyManagement.error.network'), 'error');
        },
      }
    );
  }, [deleteTarget, deleteMutation, addToast, t]);

  const handleClearSearch = useCallback(() => {
    setKeyword('');
    setAppliedKeyword('');
    setDomain('');
    setSource('');
    setIncludeDeprecated(false);
    setPage(0);
  }, []);

  const hasFilter = !!(appliedKeyword || domain || source || includeDeprecated);
  const isLoading = listQuery.isLoading;
  const isError = listQuery.isError;
  const isEmpty = !isLoading && !isError && (listQuery.data?.content?.length ?? 0) === 0;

  const hasSimilarity = (listQuery.data?.content ?? []).some(
    (t) => t.similarity !== null && t.similarity !== undefined
  );

  // Table columns
  const columns: DataTableColumn<StandardTerm>[] = [
    {
      key: 'korean',
      header: t('terminologyManagement.term.list.column.korean'),
      width: '18%',
      render: (row) => (
        <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{row.korean}</span>
      ),
    },
    {
      key: 'englishShort',
      header: t('terminologyManagement.term.list.column.englishShort'),
      width: '14%',
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
          {row.englishShort}
        </span>
      ),
    },
    {
      key: 'englishFull',
      header: t('terminologyManagement.term.list.column.englishFull'),
      width: '18%',
      render: (row) => (
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-xs)' }}>
          {row.englishFull ?? '-'}
        </span>
      ),
    },
    {
      key: 'domain',
      header: t('terminologyManagement.term.list.column.domain'),
      width: '10%',
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {row.domain ?? '-'}
        </span>
      ),
    },
    {
      key: 'source',
      header: t('terminologyManagement.term.list.column.source'),
      width: '10%',
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)' }}>
          {row.source}
        </span>
      ),
    },
    ...(hasSimilarity
      ? [
          {
            key: 'similarity',
            header: t('terminologyManagement.term.list.column.similarity'),
            width: '8%',
            render: (row: StandardTerm) => <SimilarityBadge similarity={row.similarity} />,
          } as DataTableColumn<StandardTerm>,
        ]
      : []),
    {
      key: 'status',
      header: t('terminologyManagement.term.list.column.status'),
      width: '8%',
      render: (row) => (
        <StatusPill
          tone={
            row.status === 'ACTIVE'
              ? 'success'
              : row.status === 'DEPRECATED'
              ? 'neutral'
              : 'warning'
          }
        >
          {t(`terminologyManagement.status.${row.status}`)}
        </StatusPill>
      ),
    },
    ...(isAdmin
      ? [
          {
            key: '_actions',
            header: '',
            width: '6%',
            render: (row: StandardTerm) => (
              <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  aria-label={`${t('terminologyManagement.term.list.rowMenu.edit')} — ${row.korean}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFormOpen(row);
                  }}
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-2)',
                    fontSize: 'var(--font-size-xs)',
                    background: 'var(--color-surface-muted)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {t('terminologyManagement.term.list.rowMenu.edit')}
                </button>
                <button
                  type="button"
                  aria-label={`${t('terminologyManagement.term.list.rowMenu.deprecate')} — ${row.korean}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(row);
                  }}
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-2)',
                    fontSize: 'var(--font-size-xs)',
                    background: 'var(--color-surface-muted)',
                    border: '1px solid var(--color-danger)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    color: 'var(--color-danger)',
                  }}
                >
                  {t('terminologyManagement.term.list.rowMenu.deprecate')}
                </button>
              </div>
            ),
          } as DataTableColumn<StandardTerm>,
        ]
      : []),
  ];

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
  };

  const totalElements = listQuery.data?.totalElements ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 1;

  return (
    <div style={{ padding: 'var(--spacing-6)', maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader
        title={t('terminologyManagement.term.list.title')}
        breadcrumb={<span>{t('terminologyManagement.term.list.title')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <TerminologyDownloadMenu
              fixedType="standard"
              currentGroup={source}
              currentIncludeDeprecated={includeDeprecated}
              onSuccess={(msg) => addToast(msg)}
              onError={(msg) => addToast(msg, 'error')}
            />
            {isAdmin && (
              <button
                type="button"
                data-testid="term-add-btn"
                onClick={() => handleFormOpen(null)}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                + {t('terminologyManagement.term.list.button.add')}
              </button>
            )}
          </div>
        }
      />

      {/* Subtitle */}
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 'var(--spacing-1) 0 var(--spacing-4)' }}>
        {t('terminologyManagement.term.list.subtitle', {
          totalTerms: totalElements.toLocaleString(),
        })}
      </p>

      {/* Search / Filter bar */}
      <div
        role="search"
        aria-label={t('terminologyManagement.term.list.title')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'center',
        }}
      >
        <label htmlFor="term-search" style={{ display: 'none' }}>
          {t('terminologyManagement.term.list.search.placeholder')}
        </label>
        <input
          id="term-search"
          type="search"
          placeholder={t('terminologyManagement.term.list.search.placeholder')}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 200px', minWidth: 180 }}
        />

        <select
          aria-label={t('terminologyManagement.term.list.filter.source')}
          value={source}
          onChange={(e) => { setSource(e.target.value); setPage(0); }}
          style={{ ...inputStyle, minWidth: 120 }}
        >
          <option value="">{t('terminologyManagement.term.list.filter.all')} {t('terminologyManagement.term.list.filter.source')}</option>
          {SOURCE_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeDeprecated}
            onChange={(e) => { setIncludeDeprecated(e.target.checked); setPage(0); }}
          />
          {t('terminologyManagement.term.list.filter.includeDeprecated')}
        </label>

        {hasFilter && (
          <button
            type="button"
            onClick={handleClearSearch}
            style={{
              ...inputStyle,
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              background: 'var(--color-surface-muted)',
            }}
          >
            {t('terminologyManagement.list.empty.button.clear')}
          </button>
        )}
      </div>

      {/* Content area — 5 states */}
      {isLoading && <SkeletonRows count={size} />}

      {isError && (
        <ErrorState
          title={t('terminologyManagement.error.network')}
          message={(listQuery.error as unknown as ApiError)?.message ?? t('terminologyManagement.error.network')}
          onRetry={() => void listQuery.refetch()}
        />
      )}

      {isEmpty && (
        <EmptyState
          title={t('terminologyManagement.list.empty.title')}
          description={t('terminologyManagement.list.empty.description')}
          action={
            hasFilter ? (
              <button
                type="button"
                onClick={handleClearSearch}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('terminologyManagement.list.empty.button.clear')}
              </button>
            ) : undefined
          }
        />
      )}

      {/* Default: DataTable */}
      {!isLoading && !isError && !isEmpty && (
        <DataTable
          columns={columns}
          rows={listQuery.data?.content ?? []}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/admin/terminology/standard-terms/${row.id}`)}
        />
      )}

      {/* Pagination */}
      {!isLoading && !isError && totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'var(--spacing-3)',
            marginTop: 'var(--spacing-4)',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--spacing-1)', alignItems: 'center' }}>
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              aria-label="이전 페이지"
              style={{
                padding: 'var(--spacing-1) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                opacity: page === 0 ? 0.5 : 1,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              ◀
            </button>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', padding: '0 var(--spacing-2)' }}>
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              aria-label="다음 페이지"
              style={{
                padding: 'var(--spacing-1) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                opacity: page >= totalPages - 1 ? 0.5 : 1,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              ▶
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <label htmlFor="page-size-select" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              페이지당
            </label>
            <select
              id="page-size-select"
              value={size}
              onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
              style={{
                padding: 'var(--spacing-1) var(--spacing-2)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-xs)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              총 {totalElements.toLocaleString(i18n.language === 'en' ? 'en-US' : 'ko-KR')}건
            </span>
          </div>
        </div>
      )}

      {/* Dialog state: StandardTermFormDialog */}
      <StandardTermFormDialog
        open={formOpen}
        editTarget={formTarget}
        isLoading={upsertMutation.isPending}
        errorMessage={formError}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
      />

      {/* Dialog state: DeleteConfirmDialog */}
      <DeleteConfirmDialog
        open={deleteTarget !== null}
        name={deleteTarget?.korean ?? ''}
        isLoading={deleteMutation.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />

      <TerminologyToast messages={messages} onDismiss={dismissToast} />
    </div>
  );
}
